import React, { useEffect } from 'react';
import { useDialogStore } from '@/hooks/useDialog';
import { ChapterDialog } from './ChapterDialog';
import { MindMapDialog } from './MindMapDialog';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormDialog } from "@/components/ui/form-dialog";
import { CopilotWarningDialog } from './CopilotWarningDialog';

// Simple chapter dialog for title-only editing (used in chapters list)
const SimpleChapterDialog: React.FC<{
  onSave: (data: { title: string }) => void;
  onClose: () => void;
  data: {
    defaultValues?: { title: string };
    isEditing?: boolean;
  };
}> = ({ onSave, onClose, data }) => {
  const defaultValues = data?.defaultValues || { title: '' };
  const isEditing = data?.isEditing || false;
  const [title, setTitle] = React.useState(defaultValues.title);

  // Reset form state when defaultValues change
  React.useEffect(() => {
    setTitle(defaultValues.title);
  }, [defaultValues.title]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ title });
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{isEditing ? 'Edit Chapter' : 'Create New Chapter'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Chapter title"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!title.trim()}>
            {isEditing ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};

export function RegisterDialogs() {
  const { registerDialog } = useDialogStore();

  useEffect(() => {
    // Register all dialogs
    registerDialog('chapter', ChapterDialog);
    registerDialog('edit-chapter', SimpleChapterDialog);
    registerDialog('edit-mind-map', MindMapDialog);
    registerDialog('confirm-delete', ConfirmDeleteDialog);
    registerDialog('form', FormDialog);
    registerDialog('copilot-warning', CopilotWarningDialog);
  }, [registerDialog]);

  // This component doesn't render anything
  return null;
}
